import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LearningPathCard } from './LearningPathCard';
import { ALL_LEARNING_TRACKS } from '@/data/learningTracks';
import { useNavigate } from 'react-router-dom';
import { 
  Rocket, 
  Target, 
  Trophy, 
  Users,
  ArrowRight,
  BookOpen,
  Clock,
  Star
} from 'lucide-react';

export const LearningPathsOverview: React.FC = () => {
  const navigate = useNavigate();
  const [selectedTrack, setSelectedTrack] = useState<string>('all');

  const handleEnrollInTrack = (trackId: string) => {
    // Navigate to track detail page or enrollment flow
    navigate(`/learning-paths/${trackId}`);
  };

  const handleContinueTrack = (trackId: string) => {
    // Navigate to current pathway in track
    navigate(`/learning-paths/${trackId}/continue`);
  };

  const filteredTracks = selectedTrack === 'all' 
    ? ALL_LEARNING_TRACKS 
    : ALL_LEARNING_TRACKS.filter(track => track.type === selectedTrack);

  const trackTypes = [...new Set(ALL_LEARNING_TRACKS.map(track => track.type))];

  return (
    <div className="container mx-auto px-4 py-8 space-y-8">
      {/* Header Section */}
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center gap-2 mb-4">
          <Rocket className="w-8 h-8 text-primary" />
          <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
            Learning Paths
          </h1>
        </div>
        <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
          Structured learning journeys designed to take you from beginner to expert. 
          Choose your path and master in-demand skills with our comprehensive tracks.
        </p>
      </div>

      {/* Stats Section */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="text-center">
          <CardContent className="pt-6">
            <div className="flex items-center justify-center mb-2">
              <BookOpen className="w-8 h-8 text-primary" />
            </div>
            <div className="text-2xl font-bold text-foreground">5</div>
            <p className="text-sm text-muted-foreground">Learning Tracks</p>
          </CardContent>
        </Card>
        
        <Card className="text-center">
          <CardContent className="pt-6">
            <div className="flex items-center justify-center mb-2">
              <Target className="w-8 h-8 text-primary" />
            </div>
            <div className="text-2xl font-bold text-foreground">25+</div>
            <p className="text-sm text-muted-foreground">Learning Pathways</p>
          </CardContent>
        </Card>
        
        <Card className="text-center">
          <CardContent className="pt-6">
            <div className="flex items-center justify-center mb-2">
              <Clock className="w-8 h-8 text-primary" />
            </div>
            <div className="text-2xl font-bold text-foreground">6-12</div>
            <p className="text-sm text-muted-foreground">Months to Master</p>
          </CardContent>
        </Card>
        
        <Card className="text-center">
          <CardContent className="pt-6">
            <div className="flex items-center justify-center mb-2">
              <Trophy className="w-8 h-8 text-primary" />
            </div>
            <div className="text-2xl font-bold text-foreground">95%</div>
            <p className="text-sm text-muted-foreground">Success Rate</p>
          </CardContent>
        </Card>
      </div>

      {/* Filter Tabs */}
      <Tabs value={selectedTrack} onValueChange={setSelectedTrack} className="w-full">
        <TabsList className="grid w-full grid-cols-6 mb-8">
          <TabsTrigger value="all" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            All Tracks
          </TabsTrigger>
          {trackTypes.map((type) => (
            <TabsTrigger key={type} value={type} className="capitalize">
              {type}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value={selectedTrack} className="space-y-6">
          {/* Featured Path */}
          {selectedTrack === 'all' && (
            <Card className="border-primary/20 bg-gradient-to-r from-primary/5 via-transparent to-primary/5">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-primary" />
                  <CardTitle className="text-primary">Featured Learning Path</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6 items-center">
                  <div className="space-y-4">
                    <h3 className="text-2xl font-bold text-foreground">Full-Stack Development</h3>
                    <p className="text-muted-foreground">
                      The most comprehensive path covering both frontend and backend development. 
                      Build complete applications from start to finish.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="secondary">React</Badge>
                      <Badge variant="secondary">Node.js</Badge>
                      <Badge variant="secondary">Database</Badge>
                      <Badge variant="secondary">Deployment</Badge>
                    </div>
                    <Button 
                      onClick={() => handleEnrollInTrack('fullstack')}
                      size="lg"
                      className="group"
                    >
                      Start Full-Stack Journey
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </div>
                  <div className="text-center space-y-4">
                    <div className="text-6xl">🚀</div>
                    <div className="space-y-2">
                      <div className="text-2xl font-bold text-primary">10-12 months</div>
                      <div className="text-sm text-muted-foreground">Estimated completion time</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Learning Track Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredTracks.map((track) => (
              <LearningPathCard
                key={track.id}
                track={track}
                progress={0} // Would come from user progress
                isEnrolled={false} // Would come from user enrollment status
                onEnroll={() => handleEnrollInTrack(track.id)}
                onContinue={() => handleContinueTrack(track.id)}
              />
            ))}
          </div>

          {filteredTracks.length === 0 && (
            <Card className="text-center py-12">
              <CardContent>
                <div className="text-4xl mb-4">📚</div>
                <h3 className="text-xl font-semibold mb-2">No tracks found</h3>
                <p className="text-muted-foreground">
                  No learning tracks match your current filter. Try selecting "All Tracks" to see all available options.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Call to Action */}
      <Card className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border-primary/20">
        <CardContent className="text-center py-8">
          <h3 className="text-2xl font-bold text-foreground mb-4">Ready to Start Your Journey?</h3>
          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
            Take our quick assessment to find the perfect learning path for your goals and current skill level.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => navigate('/assessment')}>
              Take Skill Assessment
            </Button>
            <Button variant="outline" size="lg" onClick={() => {
              sessionStorage.setItem('courseNavigationOrigin', 'courses');
              navigate('/courses');
            }}>
              Browse All Courses
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};